import React from 'react';
import { X, Folder, BookOpen, ShieldCheck, ShieldOff, Cpu, MessageSquare, Briefcase, ToggleRight, ExternalLink } from 'lucide-react';
import { Card } from '@os/components/ui/card';
import { Separator } from '@os/components/ui/separator';
import { Badge } from '@os/components/ui/badge';
import { Button } from '@os/components/ui/button';

export type MapleWordContextInfo = {
  source?: 'project' | 'chat';
  projectName?: string;
  chatTitle?: string;
  knowledgeBaseFolder?: string;
  modelsInfo?: string;
  trustEnabled?: boolean;
  osAccessEnabled?: boolean;
  modelsUsed?: string[];
  // NEW: identifiers to enable deep links
  chatId?: string;
  projectId?: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  info: MapleWordContextInfo;
};

const labelText = (info: MapleWordContextInfo) => {
  if (info.source === 'project' && info.projectName) return `Project: ${info.projectName}`;
  if (info.source === 'chat' && info.chatTitle) return `Chat: ${info.chatTitle}`;
  return 'Context';
};

const ContextIcon = ({ info }: { info: MapleWordContextInfo }) => {
  if (info.source === 'project') return <Briefcase className="w-4 h-4 text-foreground" />;
  if (info.source === 'chat') return <MessageSquare className="w-4 h-4 text-foreground" />;
  return <ToggleRight className="w-4 h-4 text-foreground" />;
};

const MapleWordInfoPane: React.FC<Props> = ({ open, onClose, info }) => {
  if (!open) return null;

  const {
    knowledgeBaseFolder = '—',
    modelsInfo = 'Based on Subscription Plan',
    trustEnabled = false,
    osAccessEnabled = false,
    modelsUsed = [],
    chatId,
    projectId,
  } = info || {};

  const openInChat = () => {
    if (!chatId) return;
    window.open(`/chat/${chatId}`, '_blank');
  };

  const openInProject = () => {
    if (!projectId) return;
    // There is a ProjectDetail page present; project route is /project/:id
    window.open(`/project/${projectId}`, '_blank');
  };

  return (
    <div className="h-full w-[320px] border-l border-border bg-background flex-shrink-0">
      <Card className="h-full rounded-none shadow-none border-0 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <div className="flex items-center gap-2">
            <ContextIcon info={info} />
            <h3 className="text-sm font-semibold">{labelText(info)}</h3>
          </div>
          <button
            type="button"
            className="inline-flex items-center justify-center rounded-md border border-border px-2 py-1 text-xs hover:bg-muted"
            onClick={onClose}
            aria-label="Close info pane"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-4 space-y-4 overflow-y-auto flex-1">
          {/* Project/Chat source summary */}
          <div>
            <div className="text-xs text-muted-foreground mb-1">Source</div>
            <div className="flex items-center gap-2">
              <Badge variant="secondary">
                {info.source === 'project' ? 'Project' : info.source === 'chat' ? 'Chat' : 'Unknown'}
              </Badge>
              {info.source === 'project' && info.projectName ? (
                <span className="text-sm">{info.projectName}</span>
              ) : info.source === 'chat' && info.chatTitle ? (
                <span className="text-sm">{info.chatTitle}</span>
              ) : null}
            </div>
          </div>

          <Separator />

          {/* Knowledge Base Folder */}
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <BookOpen className="w-4 h-4 text-foreground" />
              <span className="text-sm font-medium">Knowledge Base Folder</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Folder className="w-4 h-4 text-muted-foreground" />
              <span className="truncate">{knowledgeBaseFolder}</span>
            </div>
          </div>

          <Separator />

          {/* Models Used */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Cpu className="w-4 h-4 text-foreground" />
              <span className="text-sm font-medium">Models Used</span>
            </div>
            {modelsUsed.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {modelsUsed.map((m, idx) => (
                  <Badge key={`${m}-${idx}`} variant="secondary" className="text-xs">
                    {m}
                  </Badge>
                ))}
              </div>
            ) : (
              <div className="text-xs text-muted-foreground">No model data yet.</div>
            )}
            <div className="text-xs text-muted-foreground">{modelsInfo}</div>
          </div>

          <Separator />

          {/* TRUST and OS ACCESS status */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              {trustEnabled ? (
                <ShieldCheck className="w-4 h-4 text-green-600" />
              ) : (
                <ShieldOff className="w-4 h-4 text-muted-foreground" />
              )}
              <span className="text-sm font-medium">TRUST</span>
              <Badge variant={trustEnabled ? 'default' : 'outline'}>
                {trustEnabled ? 'Enabled' : 'Disabled'}
              </Badge>
            </div>
            <div className="flex items-center gap-2">
              <Cpu className="w-4 h-4 text-foreground" />
              <span className="text-sm font-medium">OS ACCESS</span>
              <Badge variant={osAccessEnabled ? 'default' : 'outline'}>
                {osAccessEnabled ? 'Enabled' : 'Disabled'}
              </Badge>
            </div>
          </div>
        </div>

        {/* Footer actions */}
        <div className="border-t border-border p-3 flex gap-2">
          {chatId && (
            <Button
              variant="outline"
              size="sm"
              className="w-full justify-center"
              onClick={openInChat}
              title="Open this document's chat"
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              Return to Chat
              <ExternalLink className="w-3 h-3 ml-2 opacity-70" />
            </Button>
          )}
          {projectId && (
            <Button
              variant="outline"
              size="sm"
              className="w-full justify-center"
              onClick={openInProject}
              title="Open this document's project"
            >
              <Briefcase className="w-4 h-4 mr-2" />
              Return to Project
              <ExternalLink className="w-3 h-3 ml-2 opacity-70" />
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
};

export default MapleWordInfoPane;